import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './zoning-permit-form.css';

function ZoningCommercialPermitApplication() {
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    // Applicant & Property
    propertyAddress: '',
    taxMap: '',
    permitRequestedBy: '',
    relationshipToOwner: '', // Owner | Contractor | Agent | Design Professional
    applicantAddress: '',
    applicantCity: '',
    applicantState: '',
    applicantZip: '',
    phone: '',
    fax: '',
    email: '',

    // Contractor
    contractorStateLicense: '',
    contractorLocalLicense: '',
    otherLocality: '', // If not City of Riverton (GTS)

    // Project
    typeProject: '', // New Construction | Addition | Repair/Remodel | Other
    totalCost: '',
    description: '',

    // Building Data
    sqftHeated: '',
    stories: '',
    sqftUnheated: '',
    sprinklered: '', // Yes | No
    sqftSprinklered: '',
    fireAlarm: '', // Yes | No
    elevators: '',
    existingUse: '',
    proposedUse: '',
    constructionType: '',

    // Checklist
    ckApplicationCompleted: false,
    ckSitePlan: false,
    ckBuildingPlans: false,
    ckUtilityReceipts: false,
    ckSubcontractorList: false,
    ckAsbestosSurvey: false,
    ckFloodplainCertificate: false,

    // Declaration
    certify: false,
    signature: '',
    signedDate: '',
  });

  const onlyDigits = (s) => (s || '').replace(/\D+/g, '');
  const isFiveZip = (s) => /^\d{5}$/.test(s);
  const isTenPhone = (s) => /^\d{10}$/.test(onlyDigits(s));
  const isEmail = (s) => /^\S+@\S+\.\S+$/.test(s);
  const isTwoLetterState = (s) => /^[A-Za-z]{2}$/.test(s);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm((f) => ({ ...f, [name]: checked }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }
    if (name === 'applicantState') {
      setForm((f) => ({ ...f, [name]: value.toUpperCase() }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((ep) => ({ ...ep, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    // Only require the first field (propertyAddress) for demo purposes
    if (!String(form.propertyAddress || '').trim()) {
      e.propertyAddress = 'Required';
    }
    // Keep format validation for fields that are filled in (but don't require them)
    if (form.applicantState && !isTwoLetterState(form.applicantState)) e.applicantState = 'Use 2-letter code (e.g., TN)';
    if (form.applicantZip && !isFiveZip(form.applicantZip)) e.applicantZip = '5-digit ZIP only';
    if (form.phone && !isTenPhone(form.phone)) e.phone = '10 digits only';
    if (form.fax && form.fax.trim() && !isTenPhone(form.fax)) e.fax = '10 digits only';
    if (form.email && !isEmail(form.email)) e.email = 'Invalid email';
    if (form.contractorStateLicense && /\D/.test(form.contractorStateLicense)) e.contractorStateLicense = 'Digits only';
    if (form.contractorLocalLicense && /\D/.test(form.contractorLocalLicense)) e.contractorLocalLicense = 'Digits only';
    ['totalCost','sqftHeated','stories','sqftUnheated','sqftSprinklered','elevators'].forEach((k) => {
      if (form[k] && form[k].trim() && isNaN(Number(form[k]))) e[k] = 'Numbers only';
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setCompleted(true);
    }, 600);
  };

  const resetForm = () => window.location.reload();

  return (
    <section className="department-details-section">
      <div className="container gts-wide-container">
        <div className="row">
          <div className="col-12">
            <div className="department-details-content-box">
              <h4 className="department-details-title">Commercial Permit Application</h4>
              <p className="gts-form-overview">
                Apply for a commercial permit with the City of <strong>Riverton</strong>, State of GTS.
                Download the official PDF or complete the compact webform below.
                This is a demo submission—no data is stored or transmitted.
              </p>

              <div className="gts-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                <a 
                  href="/guides/StateofGTSCommercialPermit-2022.pdf" 
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#e75934',
                    textDecoration: 'none',
                    border: '1px solid #e75934',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e75934';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e75934';
                  }}
                >
                  <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                  Download Application (PDF)
                </a>
                <Link 
                  to="/department/housing/zoning-permits"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#666',
                    textDecoration: 'none',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Zoning &amp; Permits Info
                </Link>
              </div>
            </div>

            {/* FORM (hidden after completion) */}
            {!completed && (
              <form className="gts-form" onSubmit={onSubmit} noValidate>
                {/* Applicant & Property */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head">
                    <h5>Applicant &amp; Property</h5>
                  </div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2">
                      <span>Property Address <span className="gts-required">*</span></span>
                      <input name="propertyAddress" value={form.propertyAddress} onChange={onChange} />
                      {errors.propertyAddress && <em className="gts-error">{errors.propertyAddress}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Tax Map # <span className="gts-required">*</span></span>
                      <input name="taxMap" value={form.taxMap} onChange={onChange} />
                      {errors.taxMap && <em className="gts-error">{errors.taxMap}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Permit Requested by <span className="gts-required">*</span></span>
                      <input name="permitRequestedBy" value={form.permitRequestedBy} onChange={onChange} />
                      {errors.permitRequestedBy && <em className="gts-error">{errors.permitRequestedBy}</em>}
                    </label>

                    <fieldset className="gts-field gts-span-2">
                      <legend>Relationship to Property Owner <span className="gts-required">*</span></legend>
                      <div className="gts-radio-list">
                        {['Owner','Contractor','Agent','Design Professional'].map((opt) => (
                          <label key={opt} className="gts-radio">
                            <input
                              type="radio"
                              name="relationshipToOwner"
                              value={opt}
                              checked={form.relationshipToOwner === opt}
                              onChange={onChange}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                      {errors.relationshipToOwner && <em className="gts-error">{errors.relationshipToOwner}</em>}
                    </fieldset>

                    <label className="gts-field gts-span-2">
                      <span>Applicant Address <span className="gts-required">*</span></span>
                      <input name="applicantAddress" value={form.applicantAddress} onChange={onChange} />
                      {errors.applicantAddress && <em className="gts-error">{errors.applicantAddress}</em>}
                    </label>

                    <label className="gts-field">
                      <span>City <span className="gts-required">*</span></span>
                      <input name="applicantCity" value={form.applicantCity} onChange={onChange} />
                      {errors.applicantCity && <em className="gts-error">{errors.applicantCity}</em>}
                    </label>

                    <label className="gts-field">
                      <span>State (2-letter) <span className="gts-required">*</span></span>
                      <input
                        name="applicantState"
                        value={form.applicantState}
                        onChange={onChange}
                        maxLength={2}
                        placeholder="TN"
                      />
                      {errors.applicantState && <em className="gts-error">{errors.applicantState}</em>}
                    </label>

                    <label className="gts-field">
                      <span>ZIP Code (5) <span className="gts-required">*</span></span>
                      <input name="applicantZip" value={form.applicantZip} onChange={onChange} maxLength={5} />
                      {errors.applicantZip && <em className="gts-error">{errors.applicantZip}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Phone (10 digits) <span className="gts-required">*</span></span>
                      <input name="phone" value={form.phone} onChange={onChange} maxLength={14} />
                      {errors.phone && <em className="gts-error">{errors.phone}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Fax (10 digits)</span>
                      <input name="fax" value={form.fax} onChange={onChange} maxLength={14} />
                      {errors.fax && <em className="gts-error">{errors.fax}</em>}
                    </label>

                    <label className="gts-field gts-span-2">
                      <span>Email <span className="gts-required">*</span></span>
                      <input name="email" type="email" value={form.email} onChange={onChange} />
                      {errors.email && <em className="gts-error">{errors.email}</em>}
                    </label>
                  </div>
                </div>

                {/* Contractor / Locality */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Contractor / Locality</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Contractor's State License #</span>
                      <input name="contractorStateLicense" value={form.contractorStateLicense} onChange={onChange} />
                      {errors.contractorStateLicense && <em className="gts-error">{errors.contractorStateLicense}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Contractor's Local License #</span>
                      <input name="contractorLocalLicense" value={form.contractorLocalLicense} onChange={onChange} />
                      {errors.contractorLocalLicense && <em className="gts-error">{errors.contractorLocalLicense}</em>}
                    </label>

                    <label className="gts-field gts-span-2">
                      <span>If NOT City of Riverton (GTS), specify locality</span>
                      <input name="otherLocality" value={form.otherLocality} onChange={onChange} />
                    </label>
                  </div>
                </div>

                {/* Project */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Project</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field gts-span-2">
                      <legend>Type of Project <span className="gts-required">*</span></legend>
                      <div className="gts-radio-list">
                        {['New Construction','Addition','Repair/Remodel','Other'].map((opt) => (
                          <label key={opt} className="gts-radio">
                            <input
                              type="radio"
                              name="typeProject"
                              value={opt}
                              checked={form.typeProject === opt}
                              onChange={onChange}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                      {errors.typeProject && <em className="gts-error">{errors.typeProject}</em>}
                    </fieldset>

                    <label className="gts-field">
                      <span>Total Cost of Construction ($) <span className="gts-required">*</span></span>
                      <input name="totalCost" value={form.totalCost} onChange={onChange} />
                      {errors.totalCost && <em className="gts-error">{errors.totalCost}</em>}
                    </label>

                    <label className="gts-field gts-span-2">
                      <span>Description of Project <span className="gts-required">*</span></span>
                      <textarea name="description" rows={3} value={form.description} onChange={onChange} />
                      {errors.description && <em className="gts-error">{errors.description}</em>}
                    </label>
                  </div>
                </div>

                {/* Building Data + Applicable Codes */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Building Data &amp; Reference</h5></div>

                  <div className="gts-2col">
                    {/* Left: Building Data */}
                    <div className="gts-left">
                      <div className="gts-form-grid">
                        <label className="gts-field">
                          <span>Sq Ft – Heated</span>
                          <input name="sqftHeated" value={form.sqftHeated} onChange={onChange} />
                          {errors.sqftHeated && <em className="gts-error">{errors.sqftHeated}</em>}
                        </label>

                        <label className="gts-field">
                          <span># Stories</span>
                          <input name="stories" value={form.stories} onChange={onChange} />
                          {errors.stories && <em className="gts-error">{errors.stories}</em>}
                        </label>

                        <label className="gts-field">
                          <span>Sq Ft – Unheated</span>
                          <input name="sqftUnheated" value={form.sqftUnheated} onChange={onChange} />
                          {errors.sqftUnheated && <em className="gts-error">{errors.sqftUnheated}</em>}
                        </label>

                        <fieldset className="gts-field">
                          <legend>Sprinklered <span className="gts-required">*</span></legend>
                          <div className="gts-radio-list">
                            {['Yes','No'].map((opt) => (
                              <label key={opt} className="gts-radio">
                                <input
                                  type="radio"
                                  name="sprinklered"
                                  value={opt}
                                  checked={form.sprinklered === opt}
                                  onChange={onChange}
                                />
                                <span>{opt}</span>
                              </label>
                            ))}
                          </div>
                          {errors.sprinklered && <em className="gts-error">{errors.sprinklered}</em>}
                        </fieldset>

                        <label className="gts-field">
                          <span>Sq Ft Sprinklered</span>
                          <input name="sqftSprinklered" value={form.sqftSprinklered} onChange={onChange} />
                          {errors.sqftSprinklered && <em className="gts-error">{errors.sqftSprinklered}</em>}
                        </label>

                        <fieldset className="gts-field">
                          <legend>Fire Alarm? <span className="gts-required">*</span></legend>
                          <div className="gts-radio-list">
                            {['Yes','No'].map((opt) => (
                              <label key={opt} className="gts-radio">
                                <input
                                  type="radio"
                                  name="fireAlarm"
                                  value={opt}
                                  checked={form.fireAlarm === opt}
                                  onChange={onChange}
                                />
                                <span>{opt}</span>
                              </label>
                            ))}
                          </div>
                          {errors.fireAlarm && <em className="gts-error">{errors.fireAlarm}</em>}
                        </fieldset>

                        <label className="gts-field">
                          <span># Elevators / Escalators</span>
                          <input name="elevators" value={form.elevators} onChange={onChange} />
                          {errors.elevators && <em className="gts-error">{errors.elevators}</em>}
                        </label>

                        <label className="gts-field">
                          <span>Existing Use</span>
                          <input name="existingUse" value={form.existingUse} onChange={onChange} />
                        </label>

                        <label className="gts-field">
                          <span>Proposed Use</span>
                          <input name="proposedUse" value={form.proposedUse} onChange={onChange} />
                        </label>

                        <label className="gts-field gts-span-2">
                          <span>Construction Type</span>
                          <input name="constructionType" value={form.constructionType} onChange={onChange} />
                        </label>
                      </div>
                    </div>

                    {/* Right: Applicable Codes only */}
                    <aside className="gts-right">
                      <div className="gts-panel gts-panel-yellow">
                        <div className="gts-panel-title">Applicable Codes</div>
                        <ul className="gts-panel-list">
                          <li>2012 International Building Code (IBC)</li>
                          <li>2012 International Mechanical Code (IMC)</li>
                          <li>2012 International Plumbing Code (IPC)</li>
                          <li>2017 National Electrical Code (NEC)</li>
                          <li>2012 International Fire Code (IFC)</li>
                          <li>2012 International Fuel Gas Code (IFGC)</li>
                          <li>2012 International Existing Building Code (IEBC)</li>
                          <li>2012 International Energy Conservation Code (IECC)</li>
                          <li>2012 International Performance Code</li>
                        </ul>
                      </div>
                    </aside>
                  </div>

                  {/* Checklist for Submission BELOW the two-column area */}
                  <div className="gts-panel gts-panel-stack gts-panel-margin-top">
                    <div className="gts-panel-title">Checklist for Submission</div>
                    <div className="gts-checklist">
                      <label className="gts-check">
                        <input type="checkbox" name="ckApplicationCompleted" checked={form.ckApplicationCompleted} onChange={onChange}/>
                        <span>Application completed</span>
                      </label>
                      <label className="gts-check">
                        <input type="checkbox" name="ckSitePlan" checked={form.ckSitePlan} onChange={onChange}/>
                        <span>
                          New construction/addition: 1 set of site/plot plans to scale showing the proposed structure, all existing
                          structures, setbacks/easements, and distances to property lines/easements. An approved site plan may be required.
                        </span>
                      </label>
                      <label className="gts-check">
                        <input type="checkbox" name="ckBuildingPlans" checked={form.ckBuildingPlans} onChange={onChange}/>
                        <span>
                          1 set of building plans to scale with sufficient detail. A/E/H/I/R-1 occupancies must be sealed by a
                          GTS-registered design professional. Other occupancies &gt; 5,000 sq ft may also require sealed plans.
                          Electronic submissions accepted—contact our office.
                        </span>
                      </label>
                      <label className="gts-check">
                        <input type="checkbox" name="ckUtilityReceipts" checked={form.ckUtilityReceipts} onChange={onChange}/>
                        <span>For new buildings, provide water &amp; sewer service receipts when applicable.</span>
                      </label>
                      <label className="gts-check">
                        <input type="checkbox" name="ckSubcontractorList" checked={form.ckSubcontractorList} onChange={onChange}/>
                        <span>Subcontractor listing submitted.</span>
                      </label>
                      <label className="gts-check">
                        <input type="checkbox" name="ckAsbestosSurvey" checked={form.ckAsbestosSurvey} onChange={onChange}/>
                        <span>
                          Renovations of buildings built prior to 1985: asbestos survey required; if present, abate per EPA
                          guidelines and submit report to the State of GTS before demolition/abatement work.
                        </span>
                      </label>
                      <label className="gts-check">
                        <input type="checkbox" name="ckFloodplainCertificate" checked={form.ckFloodplainCertificate} onChange={onChange}/>
                        <span>Floodplain Elevation Certificate if in a Special Flood Hazard Area.</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Declaration */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Declaration &amp; Signature</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="certify" checked={form.certify} onChange={onChange}/>
                      <span>
                        I certify the information provided is true and accurate and the work will comply with applicable codes.
                        <span className="gts-required"> *</span>
                      </span>
                      {errors.certify && <em className="gts-error">{errors.certify}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Applicant Signature (type full name) <span className="gts-required">*</span></span>
                      <input name="signature" value={form.signature} onChange={onChange}/>
                      {errors.signature && <em className="gts-error">{errors.signature}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Date <span className="gts-required">*</span></span>
                      <input type="date" name="signedDate" value={form.signedDate} onChange={onChange}/>
                      {errors.signedDate && <em className="gts-error">{errors.signedDate}</em>}
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="gts-actions gts-actions-form">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                  <a 
                    href="/guides/StateofGTSCommercialPermit-2022.pdf" 
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#e75934',
                      textDecoration: 'none',
                      border: '1px solid #e75934',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e75934';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#e75934';
                    }}
                  >
                    <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                    Download Application (PDF)
                  </a>
                  <button type="button" className="btn btn-outline-primary" onClick={resetForm} disabled={submitting}>
                    Reset Form
                  </button>
                </div>
              </form>
            )}

            {/* Success screen */}
            {completed && (
              <div className="gts-preview success" role="status" aria-live="polite">
                <h5>🎉 Application submitted!</h5>
                <p>
                  Thank you. Your application has been recorded in this demo environment. A confirmation email would be sent in a live system.
                </p>
                <div className="gts-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                  <button 
                    onClick={() => window.print()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#666',
                      textDecoration: 'none',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Print this page
                  </button>
                  <a 
                    href="/guides/StateofGTSCommercialPermit-2022.pdf" 
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#e75934',
                      textDecoration: 'none',
                      border: '1px solid #e75934',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e75934';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#e75934';
                    }}
                  >
                    <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                    Download Application (PDF)
                  </a>
                  <Link 
                    to="/forms"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#666',
                      textDecoration: 'none',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Back to Forms &amp; Applications
                  </Link>
                </div>
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <Link to="/forms" className="gts-muted-link">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Back to Forms &amp; Applications
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ZoningCommercialPermitApplication;
